// src/components/SwingVisualization.jsx
import React from 'react';
import { 
  Box, 
  Paper, 
  Typography,
  useTheme 
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StraightIcon from '@mui/icons-material/Straight';

const SwingVisualization = ({ swingType, swingAngle, size = 200 }) => {
  const theme = useTheme();
  
  const getSwingConfig = (type) => {
    switch(type?.toLowerCase()) {
      case 'inswing':
        return {
          color: theme.palette.info.main,
          icon: <TrendingDownIcon sx={{ fontSize: 40 }} />,
          label: 'IN-SWING',
          direction: -1
        };
      case 'outswing':
        return {
          color: theme.palette.error.main,
          icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
          label: 'OUT-SWING',
          direction: 1
        };
      default:
        return {
          color: theme.palette.success.main,
          icon: <StraightIcon sx={{ fontSize: 40 }} />,
          label: 'STRAIGHT',
          direction: 0
        };
    }
  };
  
  const config = getSwingConfig(swingType);
  const angle = swingAngle || 0;
  
  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" gutterBottom align="center">
        Swing Analysis
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
        {/* Swing Arrow Visualization */}
        <Box sx={{ 
          position: 'relative', 
          width: size, 
          height: size * 0.5,
          overflow: 'hidden'
        }}>
          {/* Pitch line */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: 2,
            backgroundColor: theme.palette.grey[400],
            transform: 'translateY(-50%)'
          }} />
          
          {/* Swing path */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '10%',
              width: '80%',
              height: 4,
              backgroundColor: config.color,
              borderRadius: 2,
              transform: `translateY(-50%) rotate(${angle * config.direction}deg)`,
              transformOrigin: 'left center',
              '&:after': {
                content: '""',
                position: 'absolute',
                right: -8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderLeft: `12px solid ${config.color}`
              }
            }}
          />
          
          {/* Ball at start */}
          <Box sx={{
            position: 'absolute',
            left: '10%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            border: `2px solid ${theme.palette.common.white}`,
            boxShadow: 2
          }} />
          
          {/* Ball at end */}
          <Box sx={{
            position: 'absolute',
            right: '10%',
            top: '50%',
            transform: 'translate(50%, -50%)',
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: config.color,
            border: `2px solid ${theme.palette.common.white}`,
            boxShadow: 2
          }} />
        </Box>
        
        {/* Swing Type Indicator */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 2,
          mt: 3
        }}>
          {config.icon}
          <Typography 
            variant="h5" 
            sx={{ 
              color: config.color,
              fontWeight: 'bold'
            }}
          >
            {config.label}
          </Typography>
        </Box>
        
        {/* Angle Display */}
        <Box sx={{ 
          backgroundColor: theme.palette.background.default,
          p: 3,
          borderRadius: 2,
          mt: 3,
          textAlign: 'center',
          width: '100%'
        }}>
          <Typography variant="h2" color={config.color} fontWeight="bold">
            {Math.abs(angle).toFixed(1)}°
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Swing Angle
          </Typography>
          
          {/* Angle comparison */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Typical swing angles:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Minimal
                </Typography>
                <Typography variant="body2">0-2°</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Moderate
                </Typography>
                <Typography variant="body2">2-5°</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Significant
                </Typography>
                <Typography variant="body2">5+°</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default SwingVisualization;